import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Smartphone, 
  Monitor, 
  MapPin, 
  Clock, 
  Calendar, 
  Trash2,
  AlertTriangle
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { twoFactorService, TrustedDevice } from "@/services/twoFactorService";
import { getCurrentSessionId } from "@/services/sessionService";
import { supabase } from "@/integrations/supabase/client";

interface TrustedDevicesManagerProps {
  userId: string;
}

const TrustedDevicesManager = ({ userId }: TrustedDevicesManagerProps) => {
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [isRevokingAll, setIsRevokingAll] = useState(false);
  const currentDeviceId = getCurrentSessionId();

  useEffect(() => {
    loadDevices();

    // Subscribe to realtime changes with Safari protection
    let channel: ReturnType<typeof supabase.channel> | null = null;

    try {
      channel = supabase
        .channel('trusted-devices-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'trusted_devices',
            filter: `user_id=eq.${userId}`
          },
          () => {
            loadDevices();
          }
        )
        .subscribe();
    } catch (error) {
      console.warn('Failed to subscribe to trusted devices (Safari security restriction?):', error);
    }

    return () => {
      try {
        if (channel) supabase.removeChannel(channel);
      } catch (error) {
        console.warn('Error cleaning up trusted devices subscription:', error);
      }
    };
  }, [userId]);

  const loadDevices = async () => {
    try {
      const trustedDevices = await twoFactorService.getTrustedDevices(userId);
      // Mark current device
      const devicesWithCurrent = trustedDevices.map(device => ({
        ...device,
        is_current: device.device_id === currentDeviceId
      }));
      setDevices(devicesWithCurrent);
    } catch (error) {
      console.error('Error loading trusted devices:', error);
      toast.error('Failed to load trusted devices');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeDevice = async (deviceId: string) => {
    if (!confirm('Are you sure you want to remove this trusted device? You will need to verify 2FA again on this device.')) {
      return;
    }

    try {
      setRevokingId(deviceId);
      await twoFactorService.revokeTrustedDevice(deviceId);
      toast.success('Device removed from trusted devices');
      loadDevices();
    } catch (error) {
      console.error('Error revoking device:', error);
      toast.error('Failed to remove trusted device');
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevokeAllDevices = async () => {
    if (!confirm('Are you sure you want to remove ALL trusted devices? You will need to verify 2FA again on all devices.')) {
      return;
    }

    try {
      setIsRevokingAll(true);
      await twoFactorService.revokeAllTrustedDevices(userId);
      toast.success('All trusted devices have been removed');
      loadDevices();
    } catch (error) {
      console.error('Error revoking all devices:', error);
      toast.error('Failed to remove trusted devices');
    } finally {
      setIsRevokingAll(false);
    }
  };

  const getDeviceIcon = (deviceType: string) => {
    if (deviceType?.toLowerCase().includes('mobile') || deviceType?.toLowerCase().includes('phone')) {
      return <Smartphone className="h-5 w-5 text-muted-foreground" />;
    }
    return <Monitor className="h-5 w-5 text-muted-foreground" />;
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return 'Unknown';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            No trusted devices. When you check "Remember this device" during 2FA verification, it will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {devices.map((device) => (
        <Card key={device.id} className={device.is_current ? 'border-primary/50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-muted rounded-lg">
                {getDeviceIcon(device.device_type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">
                    {device.browser || 'Unknown Browser'} on {device.os || 'Unknown OS'}
                  </span>
                  {device.is_current && (
                    <Badge variant="default" className="text-xs">
                      Current
                    </Badge>
                  )}
                </div>
                
                <div className="mt-1 space-y-1 text-xs text-muted-foreground">
                  {device.ip_address && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span>{device.ip_address}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Last used: {formatDate(device.last_used_at)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>Expires: {formatDate(device.expires_at)}</span>
                  </div>
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRevokeDevice(device.id)}
                disabled={revokingId === device.id || device.is_current}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                title={device.is_current ? "Cannot revoke current device" : "Revoke trusted device"}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      
      {devices.length > 1 && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleRevokeAllDevices}
          disabled={isRevokingAll}
          className="w-full text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {isRevokingAll ? 'Removing...' : 'Remove All Trusted Devices'}
        </Button>
      )}
    </div>
  );
};

export default TrustedDevicesManager;
